import { useState } from 'react';

import { Button, Card, Icon } from '@ds/core';
import { AIStatePill } from '@ds/ai';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Select } from '@ds/forms';
import { horaDe } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import type { ModoDaNota } from '@/data/notaDeIa';
import type { ModeloDeFicha } from '@/data/tipos';
import type { NotaDaIa } from './useNotaDaIa';

interface NotaDaIaCardProps {
    nota: NotaDaIa;
    modelos: ModeloDeFicha[];
    consultaAberta: boolean;
    offline: boolean;
    /** A revisão (etapa 4) abre daqui. Sem ela, o botão não aparece. */
    onRevisar?: (notaId: string) => void;
}

function relogio(segundos: number): string {
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = segundos % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * A nota da consulta pela IA, no editor de atendimento.
 *
 * O cartão mostra UMA coisa por vez: os dois botões de começar, a gravação em
 * curso, a nota sendo escrita, a nota pronta para revisão ou a falha. As notas
 * já aprovadas ficam numa linha no fim.
 */
export function NotaDaIaCard({ nota, modelos, consultaAberta, offline, onRevisar }: NotaDaIaCardProps) {
    const aviso = useAviso();
    const [pedindo, setPedindo] = useState<ModoDaNota | null>(null);
    const [descartando, setDescartando] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const { aberta } = nota;
    const aprovadas = nota.notas.filter((n) => n.situacao === 'aprovada');

    const agir = async (acao: () => Promise<void>, falha: string) => {
        setOcupado(true);
        try {
            await acao();
        } catch (e) {
            aviso({ title: falha, description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    let corpo;
    if (nota.gravando) {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                        aria-hidden
                        style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: nota.pausado ? 'var(--warning-strong)' : 'var(--danger-strong)',
                        }}
                    />
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600 }}>
                        {nota.pausado ? 'Gravação pausada' : aberta?.modo === 'consulta' ? 'Gravando a consulta' : 'Gravando o ditado'}
                    </span>
                    <span aria-live="off" style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                        {relogio(nota.segundos)}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {nota.pausado ? (
                        <Button size="sm" variant="secondary" iconLeft={<Icon name="mic" size={14} />} onClick={nota.retomar}>Retomar</Button>
                    ) : (
                        <Button size="sm" variant="secondary" iconLeft={<Icon name="pause" size={14} />} onClick={nota.pausar}>Pausar</Button>
                    )}
                    <Button size="sm" iconLeft={<Icon name="square" size={14} />} onClick={() => void nota.concluir()} loading={nota.concluindo}>
                        Concluir e gerar nota
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDescartando(true)}>Descartar</Button>
                </div>
            </div>
        );
    } else if (nota.concluindo || aberta?.situacao === 'gerando') {
        corpo = (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} role="status">
                <AIStatePill state="executando" label={nota.pendentes > 0 ? 'Enviando o áudio' : 'Escrevendo a nota'} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {nota.pendentes > 0
                        ? `${nota.pendentes} ${nota.pendentes === 1 ? 'parte' : 'partes'} para transcrever.`
                        : 'A IA está lendo a transcrição. Pode continuar a consulta.'}
                </span>
            </div>
        );
    } else if (aberta?.situacao === 'gravando') {
        /* A nota grava no servidor, mas não há microfone ligado aqui: a aba recarregou, ou o microfone caiu. */
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    A gravação foi interrompida em {relogio(nota.segundos)}.
                    {nota.pendentes > 0 ? ` ${nota.pendentes} ${nota.pendentes === 1 ? 'parte ainda está' : 'partes ainda estão'} neste aparelho.` : ''}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {consultaAberta && (
                        <Button size="sm" variant="secondary" iconLeft={<Icon name="mic" size={14} />} disabled={ocupado}
                            onClick={() => void agir(nota.continuar, 'Não foi possível continuar a gravação')}>
                            Continuar gravando
                        </Button>
                    )}
                    <Button size="sm" onClick={() => void nota.concluir()} loading={nota.concluindo} disabled={offline}>Concluir e gerar nota</Button>
                    <Button size="sm" variant="ghost" onClick={() => setDescartando(true)}>Descartar</Button>
                </div>
            </div>
        );
    } else if (aberta?.situacao === 'revisao') {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AIStatePill state="confirmar" label="Pronta para revisão" />
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    Nada foi para o prontuário ainda. Confira a ficha e o resumo antes de aprovar.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {onRevisar && <Button size="sm" onClick={() => onRevisar(aberta.id)}>Revisar e aprovar</Button>}
                    <Button size="sm" variant="ghost" onClick={() => setDescartando(true)}>Descartar</Button>
                </div>
            </div>
        );
    } else if (aberta?.situacao === 'falhou') {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <AIStatePill state="falhou" label="A nota não saiu" />
                {aberta.erro && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{aberta.erro}</p>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button size="sm" onClick={() => void nota.concluir()} loading={nota.concluindo} disabled={offline}>Tentar de novo</Button>
                    <Button size="sm" variant="ghost" onClick={() => setDescartando(true)}>Descartar</Button>
                </div>
            </div>
        );
    } else if (consultaAberta) {
        corpo = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    A IA escuta, preenche a ficha e resume a consulta. Você revisa antes de ir para o prontuário.
                </p>
                {nota.suportado ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="sm" iconLeft={<Icon name="mic" size={14} />} onClick={() => setPedindo('consulta')} disabled={offline}>Gravar consulta</Button>
                        <Button size="sm" variant="secondary" iconLeft={<Icon name="notebook-pen" size={14} />} onClick={() => setPedindo('ditado')} disabled={offline}>Ditar nota</Button>
                    </div>
                ) : (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>Este navegador não grava áudio.</p>
                )}
            </div>
        );
    }

    if (!corpo && aprovadas.length === 0) return null;

    return (
        <Card title="Nota da IA" padding={18}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {corpo}
                {nota.erro && <Alert tone="warning" dismissible={false}>{nota.erro}</Alert>}
                {aprovadas.length > 0 && (
                    <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--success-strong)' }}>
                        <Icon name="check" size={13} />
                        {aprovadas.length === 1
                            ? `Nota aprovada às ${horaDe(aprovadas[0]?.decididaEm ?? '')}, no prontuário.`
                            : `${aprovadas.length} notas aprovadas nesta consulta, no prontuário.`}
                    </p>
                )}
            </div>

            {pedindo && (
                <ComecarNotaModal
                    modo={pedindo} modelos={modelos}
                    onFechar={() => setPedindo(null)}
                    onComecar={async (opcoes) => {
                        await nota.comecar(pedindo, opcoes);
                        setPedindo(null);
                    }}
                />
            )}
            <Modal
                open={descartando} tone="danger" width={440}
                title="Descartar a nota da IA?"
                description="A transcrição e a sugestão são apagadas. Nada disso vai para o prontuário."
                onClose={() => setDescartando(false)}
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => setDescartando(false)}>Manter</Button>
                        <Button variant="danger" loading={ocupado} onClick={() => void agir(async () => {
                            await nota.descartar();
                            setDescartando(false);
                            aviso({ title: 'Nota descartada' });
                        }, 'Não foi possível descartar')}>
                            Descartar
                        </Button>
                    </>
                )}
            />
        </Card>
    );
}

interface ComecarNotaModalProps {
    modo: ModoDaNota;
    modelos: ModeloDeFicha[];
    onFechar: () => void;
    onComecar: (opcoes: { consentimento: boolean; modeloId?: string }) => Promise<void>;
}

const MODELO_PADRAO = '';

function ComecarNotaModal({ modo, modelos, onFechar, onComecar }: ComecarNotaModalProps) {
    const [consentimento, setConsentimento] = useState(false);
    const [modeloId, setModeloId] = useState(MODELO_PADRAO);
    const [erro, setErro] = useState<string | null>(null);
    const [indo, setIndo] = useState(false);
    const consulta = modo === 'consulta';
    const podeComecar = !consulta || consentimento;

    const comecar = async () => {
        if (!podeComecar) {
            setErro('Confirme que o paciente autorizou a gravação.');
            return;
        }
        setIndo(true);
        setErro(null);
        try {
            await onComecar({ consentimento, ...(modeloId ? { modeloId } : {}) });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível começar a gravação.');
        } finally {
            setIndo(false);
        }
    };

    return (
        <Modal
            open width={480}
            title={consulta ? 'Gravar a consulta' : 'Ditar a nota'}
            description={consulta
                ? 'A IA ouve a conversa, preenche a ficha e escreve o resumo. O áudio não fica guardado: cada minuto é transcrito e apagado.'
                : 'Fale a evolução como escreveria. A IA preenche a ficha e o resumo; o áudio não fica guardado.'}
            onClose={onFechar}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button iconLeft={<Icon name="mic" size={15} />} onClick={() => void comecar()} loading={indo} disabled={!podeComecar}>
                        Começar a gravar
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FormField label="Ficha que a IA preenche">
                    <Select
                        value={modeloId} onChange={setModeloId}
                        options={[
                            { value: MODELO_PADRAO, label: 'Nota da consulta (queixa, história, exame, avaliação, conduta)' },
                            ...modelos.filter((m) => m.nome !== 'Nota da consulta').map((m) => ({ value: m.id, label: m.nome })),
                        ]}
                    />
                </FormField>
                {consulta && (
                    <Checkbox
                        checked={consentimento} onChange={setConsentimento}
                        label="O paciente autorizou a gravação desta consulta"
                        description="O consentimento fica registrado na auditoria, com o seu nome e o horário."
                    />
                )}
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
            </div>
        </Modal>
    );
}
